import { useCallback, useState } from 'react'
import type { FocusEvent, MouseEvent, MouseEventHandler } from 'react'

export type UseOverlayActionsOptions = {
  stopPropagation?: boolean
}

export const useOverlayActions = ({
  stopPropagation = true
}: UseOverlayActionsOptions = {}) => {
  const [visible, setVisible] = useState(false)

  const show = useCallback(() => setVisible(true), [])
  const hide = useCallback(() => setVisible(false), [])

  const getRootProps = useCallback(
    () => ({
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: show,
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          hide()
        }
      },
      'data-overlay-actions-visible': visible ? 'true' : undefined
    }),
    [hide, show, visible]
  )

  const getActionProps = useCallback(
    <Element extends HTMLElement = HTMLButtonElement>(
      onClick?: MouseEventHandler<Element>
    ) => ({
      tabIndex: visible ? 0 : -1,
      'aria-hidden': visible ? undefined : true,
      onClick: (event: MouseEvent<Element>) => {
        if (stopPropagation) {
          event.stopPropagation()
        }

        onClick?.(event)
      }
    }),
    [stopPropagation, visible]
  )

  return {
    visible,
    show,
    hide,
    getRootProps,
    getActionProps
  }
}
